import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { HOST } from "../../common/constant";
import axiosInstance from "../../common/interceptor";
import { toastOptions } from "../../common/Utils";
import "./AsphaltDashboard.css";

/**
 * デバイス管理: 端末 ↔ 温度デバイス(おんどとり serial) の紐づけ編集。
 * 通信状態は温度ログの有無から簡易表示（緑=紐づけ済/データ有、灰=未）。
 */
interface IDeviceRow {
  cliID: number;
  terminalName?: string;
  owner?: string;
  deviceID?: string;
  deviceComm?: string;  // 温度デバイス通信状態 "on"/"off" (3分)
  pitaComm?: string;    // ぴたあっと通信状態 "on"/"off" (GPS送信間隔)
}
const dot = (s?: string): string => "asp-dot " + (s === "on" ? "asp-dot-on" : "asp-dot-off");
interface IProps {
  onNavi?: (path: string) => void;
}

const DeviceManagement: React.FC<IProps> = ({ onNavi }) => {
  const [rows, setRows] = useState<IDeviceRow[]>([]);

  const load = useCallback(() => {
    const user = Cookies.getJSON("user") as { siteId?: number } | undefined;
    const siteId = user?.siteId ?? 0;
    axiosInstance
      .get(HOST() + "/Form/V1/Dat/AsphaltDashboard/Devices?siteID=" + siteId)
      .then((res) => setRows((res.data?.data as IDeviceRow[]) ?? []))
      .catch(() => toast.error("データの取得に失敗しました。", toastOptions));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeId = (cliID: number, value: string) => {
    setRows((prev) => prev.map((r) => (r.cliID === cliID ? { ...r, deviceID: value } : r)));
  };

  const save = () => {
    const puts = rows.map((r) =>
      axiosInstance.put(HOST() + "/Form/V1/Dat/AsphaltDashboard/Device", { cliID: r.cliID, deviceID: r.deviceID ?? "" })
    );
    Promise.all(puts)
      .then(() => toast.success("更新に成功。", toastOptions))
      .catch(() => toast.error("更新に失敗。", toastOptions));
  };

  const close = () => {
    if (onNavi) onNavi("/dashboard");
    else window.location.href = "/dashboard";
  };

  return (
    <div className="asp-dashboard">
      <div className="asp-devhead">
        <div className="asp-title" style={{ flex: 1 }}>デバイス管理</div>
        <div>
          <button type="button" className="btn btn-primary btn-sm mar-5" onClick={close}>キャンセル</button>
          <button type="button" className="btn btn-primary btn-sm mar-5" onClick={save}>保存</button>
        </div>
      </div>

      <div className="asp-legend">
        通信中: <span className="asp-dot asp-dot-on" /> 未通信: <span className="asp-dot asp-dot-off" />
      </div>

      <div className="asp-board-wrap">
        <table className="asp-board asp-device">
          <thead>
            {/* SKY-74: 通信状態ヘッダーを上部に追加し、左「ぴたあっと」右「温度デバイス」に並べ替え
                （デバイス管理 テスト項目⑩⑪⑫）。メイン画面と同じ rowSpan/colSpan 構成にそろえる。 */}
            <tr>
              <th rowSpan={2}>端末名</th>
              <th rowSpan={2}>所有者</th>
              <th rowSpan={2}>温度デバイスID</th>
              <th colSpan={2}>通信状態</th>
            </tr>
            <tr>
              <th>ぴたあっと</th>
              <th>温度デバイス</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="asp-empty">データがありません</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.cliID}>
                  <td>{r.terminalName}</td>
                  <td>{r.owner}</td>
                  <td>
                    <input
                      type="text"
                      className="form-control asp-devinput"
                      value={r.deviceID ?? ""}
                      onChange={(e) => onChangeId(r.cliID, e.target.value)}
                    />
                  </td>
                  {/* 通信状態(設計): ぴたあっと=GPS送信間隔以内 / 温度デバイス=3分以内。バックエンド算出値を表示。 */}
                  <td><span className={dot(r.pitaComm)} /></td>
                  <td><span className={dot(r.deviceComm)} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeviceManagement;
